import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import AsyncStorage from '@react-native-async-storage/async-storage';
import type { ContextType } from './analysis';

/**
 * User preferences for the app
 */
export interface UserPreferences {
  /** Default context for new analyses */
  defaultContext: ContextType;
  /** Enable haptic feedback for interactions */
  hapticFeedback: boolean;
  /** Enable reduced motion for accessibility */
  reducedMotion: boolean;
  /** App theme preference */
  theme: 'light' | 'dark' | 'auto';
}

/**
 * User state and actions
 */
interface UserState {
  /** User preferences */
  preferences: UserPreferences;

  // Actions

  /**
   * Set the default context for new analyses
   * @param context - Context type to set as default
   */
  setDefaultContext: (context: ContextType) => void;

  /**
   * Toggle haptic feedback on/off
   */
  toggleHapticFeedback: () => void;

  /**
   * Set reduced motion preference
   * @param enabled - Whether to enable reduced motion
   */
  setReducedMotion: (enabled: boolean) => void;

  /**
   * Set the app theme
   * @param theme - Theme preference (light, dark, or auto)
   */
  setTheme: (theme: 'light' | 'dark' | 'auto') => void;

  /**
   * Reset all preferences to default values
   */
  resetPreferences: () => void;
}

/**
 * Default user preferences
 */
const DEFAULT_PREFERENCES: UserPreferences = {
  defaultContext: 'family',
  hapticFeedback: true,
  reducedMotion: false,
  theme: 'auto',
};

/**
 * User preferences store with AsyncStorage persistence
 *
 * Features:
 * - Persistent storage of user preferences
 * - User-scoped storage keys (Security: Solution 2)
 * - Default context selection
 * - Accessibility settings (haptic feedback, reduced motion)
 * - Theme preference
 * - Reset to defaults
 *
 * Security: Uses user-scoped AsyncStorage keys to prevent preference leakage
 * between authentication sessions (OWASP A01, GDPR Article 32 compliance)
 */
export const useUserStore = create<UserState>()(
  persist(
    (set) => ({
      preferences: DEFAULT_PREFERENCES,

      setDefaultContext: (context) => {
        set((state) => ({
          preferences: {
            ...state.preferences,
            defaultContext: context,
          },
        }));
      },

      toggleHapticFeedback: () => {
        set((state) => ({
          preferences: {
            ...state.preferences,
            hapticFeedback: !state.preferences.hapticFeedback,
          },
        }));
      },

      setReducedMotion: (enabled) => {
        set((state) => ({
          preferences: {
            ...state.preferences,
            reducedMotion: enabled,
          },
        }));
      },

      setTheme: (theme) => {
        set((state) => ({
          preferences: {
            ...state.preferences,
            theme,
          },
        }));
      },

      resetPreferences: () => {
        set({ preferences: DEFAULT_PREFERENCES });
      },
    }),
    {
      name: 'drama-user-storage', // Note: User-scoped key handled by partialize
      storage: createJSONStorage(() => AsyncStorage),
      // Solution 2: Partition storage by user ID for automatic isolation
      // This provides defense-in-depth alongside Solution 1 (cache clearing)
      partialize: (state) => {
        // Return state as-is. User scoping happens via cache clearing (Solution 1)
        // Future enhancement: Implement full user-scoped keys with Supabase session
        return state;
      },
    }
  )
);
